import React, { useEffect, useRef, useState } from 'react';
import { useCreateTransporterProfile } from '../../hooks/useCreateTransporterProfile';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import cloneDeep from 'lodash.clonedeep';
import classes from './Onboarding.module.css';
import OnboardingProgress from './OnboardingProgress';
import { Activity, Constants, currentRequestTimeStamp } from '../../Constants';
import { Urls } from '../../Urls';
import LocationAutoComplete from '../Shared/AutoCompleteV2/AutoCompleteV2';
import Dropdown from '../Shared/Forms/Dropdown/Dropdown';
import { FaTrash, FaPlus } from 'react-icons/fa';
import { HiOutlineUpload } from 'react-icons/hi';
import { PiTruckLight } from "react-icons/pi";
import { MdAutoGraph } from "react-icons/md";
import { LiaTruckLoadingSolid } from "react-icons/lia";
import { RiSparklingFill } from "react-icons/ri";
import { LuBriefcaseBusiness } from "react-icons/lu";
import { RiAuctionLine } from "react-icons/ri";
import { RiFileExcel2Line } from "react-icons/ri";

import firebase from '../../Firebase/firebase';
import { useAppContext, ContextConstants } from "../../Context/AppContext";
import { useOnboardingMixpanelTrack } from './hooks/useOnboardingMixpanelTrack';
import { useTransporterData } from '../../hooks/useTransporterData';
import { useCheckDuplicateTransporterName } from '../../hooks/useCheckDuplicateTransporterName';

const STEPS = ['Company Basics', 'Contact Person', 'Fleet & Vehicle Types'];

// ── helpers ──────────────────────────────────────────────────
const validEmail = e => {
    if (!e) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
};

/**
 * OnboardingQuickForm — 3-step minimal mandatory-fields form.
 * Step content is rendered INLINE (not as inner components) to avoid
 * React unmounting inputs on every state change.
 */
const OnboardingQuickForm = ({ transporterData, onSuccess, onSwitchToUpload, onSwitchToExcel }) => {
    const [step, setStep] = useState(0);

    const { checkDuplicate, loading } = useCheckDuplicateTransporterName();
    const [isDuplicateName, setIsDuplicateName] = useState(false);
    const debounceRef = useRef(null);

    // ── Step 0: Company Basics ────────────────────────────────
    const [companyName, setCompanyName] = useState('');
    const [companyType, setCompanyType] = useState('');
    const [headquarter, setHeadquarter] = useState('');
    const [zone, setZone] = useState([]);
    const [established, setEstablished] = useState('');

    // ── Step 1: Contact POC ──────────────────────────────────
    const [pocName, setPocName] = useState('');
    const [pocPhone, setPocPhone] = useState('');
    const [pocEmail, setPocEmail] = useState('');

    // ── Step 2: Fleet & Vehicle Types ────────────────────────
    const [selectedModeNames, setSelectedModeNames] = useState([]);
    const [trucks, setTrucks] = useState([{ type: '' }, { type: '' }]);
    const [ownedFleet, setOwnedFleet] = useState('');
    const [associatedFleet, setAssociatedFleet] = useState('');

    // ── Optional Financials (Step 0) ──────────────────────────
    const defaultFy = Constants.FinancialYears[1]?.value ?? '2024-2025'; // previous FY
    const [turnoverFy, setTurnoverFy] = useState(defaultFy);
    const [turnoverAmt, setTurnoverAmt] = useState('');
    const [turnoverUnit, setTurnoverUnit] = useState('CR');
    const [netProfitFy, setNetProfitFy] = useState(defaultFy);
    const [netProfitAmt, setNetProfitAmt] = useState('');
    const [netProfitUnit, setNetProfitUnit] = useState('CR');

    // ── General ──────────────────────────────────────────────
    const [errors, setErrors] = useState({});
    const { createProfile, saving, error: saveErr } = useCreateTransporterProfile();

    const { data: transporterInfo } = useTransporterData();
    const { dispatch, clientIp } = useAppContext();
    const { trackOnboardingEvent } = useOnboardingMixpanelTrack();

    const firstContact = transporterInfo?.contact_information?.[0];

    // ── Build modes_of_transportation payload ────────────────
    const buildModesPayload = () => {
        const transportationModes = Constants.TransportationModes || [];
        const result = [];
        transportationModes.forEach(trMode => {
            const subCat = (trMode.values || []).filter(v => selectedModeNames.includes(v));
            if (selectedModeNames.includes(trMode.name) || subCat.length) {
                result.push({ name: trMode.name, values: subCat });
            }
        });
        return result;
    };

    // ── Validation per step ──────────────────────────────────
    const validateStep0 = () => {
        const e = {};
        if (!companyName.trim()) e.companyName = 'Company name is required';
        if (isDuplicateName) e.companyName = 'This transporter name already exists';
        if (!headquarter.trim()) e.headquarter = 'Registered office is required';
        if (!zone.length) e.zone = 'Select at least one area of operation';
        setErrors(e);
        return !Object.keys(e).length;
    };

    const validateStep1 = () => {
        const e = {};
        if (!pocName.trim()) e.pocName = 'Contact name is required';
        // if (!pocPhone || !isValidPhoneNumber(pocPhone)) e.pocPhone = 'Valid phone number is required';
        if (!validEmail(pocEmail)) e.pocEmail = 'Valid email is required';
        setErrors(e);
        return !Object.keys(e).length;
    };

    const validateStep2 = () => {
        const e = {};
        if (!selectedModeNames.length) e.modes = 'Select at least one mode of transportation';
        const emptyTruck = trucks.find(t => !t.type.trim());
        if (emptyTruck) e.trucks = 'All truck entries must be filled';
        setErrors(e);
        return !Object.keys(e).length;
    };

    const handleNext = () => {
        setErrors({});
        const validators = [validateStep0, validateStep1, validateStep2];
        if (validators[step]()) {
            const stepLabels = ['Company Basics completed', 'Contact Person completed'];
            trackOnboardingEvent(
                'onboarding quick form',
                `Step ${step + 1}: ${stepLabels[step]}`,
                {
                    step,
                    ...(step === 0 && { company_name: companyName, zone, headquarter }),
                }
            );
            setStep(s => s + 1);
        }
    };

    const handleBack = () => {
        setErrors({});
        if (step > 0) {
            trackOnboardingEvent(
                'onboarding quick form',
                'Step navigated back',
                { from_step: step }
            );
            setStep(s => s - 1);
        }
    };

    // ── Submit ───────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!validateStep2()) return;
        const payload = {
            ...transporterData,
            transporter_name: companyName.trim(),
            company_type: companyType,
            headquarter: headquarter.trim(),
            zone,
            established: established.trim(),
            contact_information: [
                {
                    name: pocName.trim(),
                    contact_number: firstContact.contact_number,
                    email: pocEmail.trim(),
                    designation: '',
                    poc: 'yes',
                },
            ],
            modes_of_transportation: buildModesPayload(),
            truck_asset_details: trucks.filter(t => t.type.trim()),
            modes_of_operation: [
                { name: 'Contracted', percentage: '' },
                { name: 'Spot/Walkin/Adhoc', percentage: '' },
            ],
            ...(turnoverAmt !== '' && {
                turnover: [{
                    financial_year: turnoverFy,
                    amount: turnoverAmt,
                    unit: turnoverUnit,
                    currency: ['Thousands', 'Millions'].includes(turnoverUnit) ? '$' : '₹',
                }],
            }),
            ...(netProfitAmt !== '' && {
                net_profit: [{
                    financial_year: netProfitFy,
                    amount: netProfitAmt,
                    unit: netProfitUnit,
                    currency: ['Thousands', 'Millions'].includes(netProfitUnit) ? '$' : '₹',
                }],
            }),
            ...(ownedFleet !== '' && {
                owned_fleet: { amount: ownedFleet, fleet_list: '', financial_year: turnoverFy },
            }),
            ...(associatedFleet !== '' && {
                associated_fleet: { amount: associatedFleet, associated_list: null, financial_year: turnoverFy },
            }),
        };
        const { success } = await createProfile(payload);
        if (success) {
            trackOnboardingEvent(
                'onboarding quick form',
                'New transporter profile created via Quick Form',
                {
                    transporter_id: transporterInfo?.transporter_id,
                    modes_count: buildModesPayload().length,
                    trucks_count: trucks.filter(t => t.type.trim()).length,
                }
            );
            onSuccess(payload);
        } else {
            trackOnboardingEvent(
                'onboarding quick form',
                'Transporter profile creation failed',
                { error: saveErr }
            );
        }
    };

    // ── Truck helpers ────────────────────────────────────────
    const addTruck = () => setTrucks(prev => [...prev, { type: '' }]);
    const removeTruck = i => setTrucks(prev => prev.filter((_, idx) => idx !== i));
    const updateTruck = (i, val) => setTrucks(prev => prev.map((t, idx) => idx === i ? {
        ...t, type: val,
        self_owned: "",
        avg_fleet_age: "",
        planned_increase: "",
        attached_vehicles: "",
        vendors: "",
    } : t));

    // ── Options ──────────────────────────────────────────────
    const zoneOptions = Array.isArray(Constants?.AreaOfOperation)
        ? cloneDeep(Constants.AreaOfOperation) : [];
    const companyTypeOptions = Array.isArray(Constants?.CompanyTypes)
        ? cloneDeep(Constants.CompanyTypes) : [];
    const transportationModes = Constants.TransportationModes || [];
    const toggleMode = name => {
        setSelectedModeNames(prev =>
            prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
        );
        setErrors(p => ({ ...p, modes: '' }));
    };

    const isLastStep = step === STEPS.length - 1;
    const fyOptions = cloneDeep(Constants.FinancialYears);


    // Logout function
    const handleLogout = () => {
        dispatch({
            type: ContextConstants.SetData,
            payload: {},
        });
        trackOnboardingEvent(
            'onboarding quick form',
            'Transporter logged out from onboarding',
            {
                transporter_id: transporterInfo?.transporter_id,
                contact_number: transporterInfo?.user_details?.contact_number,
            }
        );

        Activity(
            [{
                screen: "transporter profile",
                module: "lorri transporter profile",
                user_activity: `Logged out successfully`,
                timestamp: currentRequestTimeStamp(),
            }],
            "Sign-Out",
            transporterInfo.transporter_name,
            transporterInfo.transporter_id,
            clientIp,
            transporterInfo?.user_details?.contact_number
        );
        firebase.logout();
    };

    // Onchange transporter name
    const handleCompanyNameChange = (value) => {
        setCompanyName(value);

        if (!value.trim()) {
            setIsDuplicateName(false);
            setErrors(prev => ({ ...prev, companyName: '' }));
            return;
        }

        // Clear previous debounce
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(async () => {
            try {
                const isDuplicate = await checkDuplicate(value.trim());
                setIsDuplicateName(isDuplicate);

                setErrors(prev => ({
                    ...prev,
                    companyName: isDuplicate ? 'This transporter name already exists' : ''
                }));
            } catch (err) {
                console.error('Duplicate check failed', err);
            }
        }, 500);
    };

    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    // ═════════════════════════════════════════════════════════
    // RENDER
    // ═════════════════════════════════════════════════════════
    return (
        <div className={classes.onboardingWrap}>
            <div className={classes.splitCard}>

                {/* ── Left Panel: Hero ── */}
                <div className={classes.leftPanel}>
                    <div className={classes.logoWrap}>
                        <img src={Urls.LorriLogoLogin} alt="LoRRI" className={classes.logo} />
                    </div>

                    <h1 className={classes.heading}>Welcome to LoRRI!</h1>
                    <p className={classes.subheading}>
                        Join India's leading transportation network.
                    </p>

                    <div className={classes.featureList}>
                        <div className={classes.featureItem}>
                            <div className={classes.featureIconWrap}><PiTruckLight /></div>
                            <div className={classes.featureText}>
                                <strong>Live Spot RFQs</strong>
                            </div>
                        </div>
                        <div className={classes.featureItem}>
                            <div className={classes.featureIconWrap}><LiaTruckLoadingSolid /></div>
                            <div className={classes.featureText}>
                                <strong>Indents & Invoices</strong>
                            </div>
                        </div>
                        <div className={classes.featureItem}>
                            <div className={classes.featureIconWrap}><RiAuctionLine /></div>
                            <div className={classes.featureText}>
                                <strong>RFQs & Auctions</strong>
                            </div>
                        </div>
                        <div className={classes.featureItem}>
                            <div className={classes.featureIconWrap}><MdAutoGraph /></div>
                            <div className={classes.featureText}>
                                <strong>Lane Intelligence</strong>
                            </div>
                        </div>

                    </div>
                </div>

                {/* ── Right Panel: Form ── */}
                <div className={classes.rightPanel}>
                    <OnboardingProgress steps={STEPS} current={step} />

                    <h1 className={`${classes.heading} mb-2`}>{STEPS[step]}</h1>
                    {/* <p className={classes.subheading}>{STEP_SUBTITLES[step]}</p> */}

                    {/* ═══ STEP 0: Company Basics ═══ */}
                    {step === 0 && (
                        <div className={classes.fieldGrid}>

                            {/* ── AI Upload Banner ── */}
                            <div className={`${classes.fieldFull} ${classes.aiBanner}  mb-2`} onClick={onSwitchToUpload}>
                                <div className={classes.aiBannerContent}>
                                    <div className={classes.aiBannerIcon}>
                                        <RiSparklingFill />
                                    </div>
                                    <div className={classes.aiBannerText}>
                                        <strong>Auto-fill with AI</strong>
                                        <span>Upload a company brochure or PDF and let AI extract your details instantly.</span>
                                    </div>
                                </div>
                                <button className={`${classes.aiBannerBtn}`} type="button">
                                    <HiOutlineUpload /> Upload
                                </button>
                            </div>

                            {/* ── Excel Template Banner ── */}
                            <div className={`${classes.fieldFull} ${classes.excelBanner} mb-3`} onClick={onSwitchToExcel}>
                                <div className={classes.aiBannerContent}>
                                    <div className={`${classes.aiBannerIcon} ${classes.excelBannerIcon}`}>
                                        <RiFileExcel2Line />
                                    </div>
                                    <div className={classes.aiBannerText}>
                                        <strong>Fill via Excel Template</strong>
                                        <span>Download our standard format, fill it offline, and upload it to import your profile.</span>
                                    </div>
                                </div>
                                <button className={`${classes.excelBannerBtn}`} type="button">
                                    Use Template
                                </button>
                            </div>

                            <div className={`${classes.fieldWrap} ${classes.fieldFull}`}>
                                <div className={classes.fieldLabel}>
                                    <span className={classes.asterisk}>*</span>Transporter Name
                                </div>
                                <input
                                    className={`${classes.textInput} ${errors.companyName ? classes.invalid : ''}`}
                                    placeholder="Your company / transporter name"
                                    value={companyName}
                                    onChange={(e) => handleCompanyNameChange(e.target.value)}
                                    id="ob-company-name"
                                />
                                {errors.companyName && <div className={classes.errorMsg}>{errors.companyName}</div>}
                                {loading && <div className={classes.checkingMsg}>Checking...</div>}
                            </div>

                            <div className={classes.fieldWrap}>
                                <div className={classes.fieldLabel}>Company Type</div>
                                <Dropdown
                                    picklistOptions={companyTypeOptions}
                                    defaultValues={companyTypeOptions.filter(o => o.value === companyType)}
                                    onValueChange={sel => setCompanyType(sel?.value || '')}
                                    placeholder="Select type"
                                    isSearchable
                                />
                            </div>

                            <div className={classes.fieldWrap}>
                                <div className={classes.fieldLabel}>
                                    <span className={classes.asterisk}>*</span>Registered Office / HQ
                                </div>
                                <LocationAutoComplete
                                    value={headquarter ? { label: headquarter, value: headquarter } : null}
                                    onChange={(selected) => {
                                        setHeadquarter(selected ? selected.value : '');
                                        setErrors(prev => ({ ...prev, headquarter: '' }));
                                    }}
                                    onClear={() => {
                                        setHeadquarter('');
                                        setErrors(prev => ({ ...prev, headquarter: '' }));
                                    }}
                                    placeholder="Search city"
                                />
                                {errors.headquarter && <div className={classes.errorMsg}>{errors.headquarter}</div>}
                            </div>

                            <div className={classes.fieldWrap}>
                                <div className={classes.fieldLabel}>
                                    <span className={classes.asterisk}>*</span>Area of Operation
                                </div>
                                <Dropdown
                                    picklistOptions={zoneOptions}
                                    defaultValues={zoneOptions.filter(o => zone.includes(o.value))}
                                    onValueChange={sel => {
                                        setZone(Array.isArray(sel) ? sel?.map(s => s?.value) : Object.keys(sel).length > 0 ? [sel] : []);
                                        setErrors(prev => ({ ...prev, zone: '' }));
                                    }}
                                    placeholder="Select regions"
                                    isMultiselect
                                    isSearchable
                                    isNewOptionValid
                                    invalid={!!errors.zone}
                                    // ErrorMessage={errors.zone}
                                />
                                {errors.zone && <div className={classes.errorMsg}>{errors.zone}</div>}
                            </div>

                            <div className={classes.fieldWrap}>
                                <div className={classes.fieldLabel}>Established Year</div>
                                <input
                                    className={classes.textInput}
                                    placeholder="e.g. 2005"
                                    value={established}
                                    maxLength={4}
                                    onChange={e => setEstablished(e.target.value.replace(/\D/g, ''))}
                                    id="ob-established"
                                />
                            </div>

                            {/* ── Financial Snapshot (Optional) ── */}
                            <div className={classes.fieldFull} style={{ marginTop: '6px' }}>
                                <div className={``}>
                                    <div className={`mb-2 ${classes.sectionLabel}`}>
                                        <LuBriefcaseBusiness style={{ color: '#393185' }} /> Company Financials (Optional)
                                    </div>

                                    {/* Turnover row */}
                                    <div className={classes.finRow}>
                                        <div className={classes.finLabel}>Turnover</div>
                                        <div className={classes.finFields}>
                                            <div>
                                                <select
                                                    className={classes.finSelect}
                                                    value={turnoverFy}
                                                    onChange={e => setTurnoverFy(e.target.value)}
                                                    id="ob-turnover-fy"
                                                >
                                                    {fyOptions.map(fy => (
                                                        <option key={fy.value} value={fy.value}>{fy.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <input
                                                className={`${classes.textInput} ${classes.finAmtInput}`}
                                                type="number"
                                                placeholder="Amount"
                                                value={turnoverAmt}
                                                min="0"
                                                onChange={e => setTurnoverAmt(e.target.value)}
                                                id="ob-turnover-amt"
                                            />
                                            <select
                                                className={classes.finUnitSelect}
                                                value={turnoverUnit}
                                                onChange={e => setTurnoverUnit(e.target.value)}
                                                id="ob-turnover-unit"
                                            >
                                                <option value="CR">₹ CR</option>
                                                <option value="Lakhs">₹ Lakhs</option>
                                                <option value="Thousands">$ Thousands</option>
                                                <option value="Millions">$ Millions</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Net Profit row */}
                                    <div className={classes.finRow}>
                                        <div className={classes.finLabel}>Net Profit</div>
                                        <div className={classes.finFields}>
                                            <div>
                                                <select
                                                    className={classes.finSelect}
                                                    value={netProfitFy}
                                                    onChange={e => setNetProfitFy(e.target.value)}
                                                    id="ob-netprofit-fy"
                                                >
                                                    {fyOptions.map(fy => (
                                                        <option key={fy.value} value={fy.value}>{fy.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <input
                                                className={`${classes.textInput} ${classes.finAmtInput}`}
                                                type="number"
                                                placeholder="Amount"
                                                value={netProfitAmt}
                                                min="0"
                                                onChange={e => setNetProfitAmt(e.target.value)}
                                                id="ob-netprofit-amt"
                                            />
                                            <select
                                                className={classes.finUnitSelect}
                                                value={netProfitUnit}
                                                onChange={e => setNetProfitUnit(e.target.value)}
                                                id="ob-netprofit-unit"
                                            >
                                                <option value="CR">₹ CR</option>
                                                <option value="Lakhs">₹ Lakhs</option>
                                                <option value="Thousands">$ Thousands</option>
                                                <option value="Millions">$ Millions</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ═══ STEP 1: Contact POC ═══ */}
                    {step === 1 && (
                        <>
                            <div className={classes.infoBanner} style={{ marginBottom: '18px' }}>
                                * Add your contact details. <em>You can add more contacts later by editing your profile</em>.
                            </div>

                            <div className={classes.fieldWrap}>
                                <div className={classes.fieldLabel}>
                                    <span className={classes.asterisk}>*</span>Full Name
                                </div>
                                <input
                                    className={`${classes.textInput} ${errors.pocName ? classes.invalid : ''}`}
                                    placeholder="Contact person name"
                                    value={pocName}
                                    onChange={e => { setPocName(e.target.value); setErrors(p => ({ ...p, pocName: '' })); }}
                                    id="ob-poc-name"
                                />
                                {errors.pocName && <div className={classes.errorMsg}>{errors.pocName}</div>}
                            </div>

                            <div className={classes.fieldWrap}>
                                <div className={classes.fieldLabel}>
                                    <span className={classes.asterisk}>*</span>Phone Number
                                </div>
                                <div className={classes.phoneWrapper}>
                                    <PhoneInput
                                        international
                                        defaultCountry={firstContact?.country_code || 'IN'}
                                        value={firstContact?.contact_number}
                                        onChange={val => { setPocPhone(val); setErrors(p => ({ ...p, pocPhone: '' })); }}
                                        disabled={true}
                                        id="ob-poc-phone"
                                    />
                                </div>
                                {errors.pocPhone && <div className={classes.errorMsg}>{errors.pocPhone}</div>}
                            </div>

                            <div className={classes.fieldWrap}>
                                <div className={classes.fieldLabel}>
                                    <span className={classes.asterisk}>*</span>Email Address
                                </div>
                                <input
                                    className={`${classes.textInput} ${errors.pocEmail ? classes.invalid : ''}`}
                                    placeholder=""
                                    value={pocEmail}
                                    type="email"
                                    onChange={e => { setPocEmail(e.target.value); setErrors(p => ({ ...p, pocEmail: '' })); }}
                                    id="ob-poc-email"
                                />
                                {errors.pocEmail && <div className={classes.errorMsg}>{errors.pocEmail}</div>}
                            </div>
                        </>
                    )}

                    {/* ═══ STEP 2: Fleet Basics ═══ */}
                    {step === 2 && (
                        <>
                            <div className={classes.fieldWrap}>
                                <div className={classes.fieldLabel}>
                                    <span className={classes.asterisk}>*</span>Modes of Transportation
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
                                    {transportationModes.map(trMode => {
                                        const isSelected = selectedModeNames.includes(trMode.name);
                                        return (
                                            <span
                                                key={trMode.name}
                                                onClick={() => toggleMode(trMode.name)}
                                                style={{
                                                    padding: '6px 14px',
                                                    borderRadius: '20px',
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    border: `1px solid ${isSelected ? '#393185' : '#dddaf0'}`,
                                                    background: isSelected ? '#393185' : '#faf9fe',
                                                    color: isSelected ? '#fff' : '#3d3a55',
                                                    transition: 'all 0.15s',
                                                    userSelect: 'none',
                                                }}
                                            >
                                                {trMode.name}
                                            </span>
                                        );
                                    })}
                                </div>
                                {errors.modes && <div className={classes.errorMsg}>{errors.modes}</div>}
                            </div>

                            <div className={classes.sectionLabel}>
                                Fleet / Truck Types
                            </div>
                            <p style={{ fontSize: '13px', color: '#6e6b8a', marginBottom: '10px' }}>
                                Add at least one type of truck / vehicle you operate.
                            </p>

                            <div className={classes.trucksContainer}>
                                {trucks.map((truck, i) => (
                                    <div className={classes.truckRow} key={i}>
                                        <input
                                            className={`${classes.textInput} ${classes.truckRowInput} ${errors.trucks && !truck.type.trim() ? classes.invalid : ''}`}
                                            placeholder="e.g. 20ft Container, Trailer, Mini Truck"
                                            value={truck.type}
                                            onChange={e => updateTruck(i, e.target.value)}
                                            id={`ob-truck-${i}`}
                                        />
                                        {trucks.length > 1 && (
                                            <button
                                                className={classes.truckRowRemove}
                                                onClick={() => removeTruck(i)}
                                                title="Remove"
                                                type="button"
                                            >
                                                <FaTrash />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                            {errors.trucks && <div className={classes.errorMsg}>{errors.trucks}</div>}

                            <button className={classes.addTruckBtn} onClick={addTruck} type="button" id="ob-add-truck">
                                <FaPlus /> <span>Add another vehicle type</span>
                            </button>

                            {/* ── Fleet Strength (Optional) ── */}
                            <div className={`mt-2`}>
                                <div className={classes.sectionLabel}>
                                    Fleet Strength
                                </div>

                                <div className={classes.finRow}>
                                    <div className={classes.finLabel}>Owned Fleet</div>
                                    <div className={classes.finFields}>
                                        <input
                                            className={`${classes.textInput} ${classes.finAmtInput}`}
                                            type="number"
                                            placeholder="No. of vehicles"
                                            value={ownedFleet}
                                            min="0"
                                            onChange={e => setOwnedFleet(e.target.value)}
                                            id="ob-owned-fleet"
                                        />
                                        <span className={classes.finUnit}>vehicles</span>
                                    </div>
                                </div>

                                <div className={classes.finRow}>
                                    <div className={classes.finLabel}>Associated Fleet</div>
                                    <div className={classes.finFields}>
                                        <input
                                            className={`${classes.textInput} ${classes.finAmtInput}`}
                                            type="number"
                                            placeholder="No. of vehicles"
                                            value={associatedFleet}
                                            min="0"
                                            onChange={e => setAssociatedFleet(e.target.value)}
                                            id="ob-associated-fleet"
                                        />
                                        <span className={classes.finUnit}>vehicles</span>
                                    </div>
                                </div>
                            </div>

                            {saveErr && <div className={classes.uploadError} style={{ marginTop: '14px' }}>{saveErr}</div>}
                        </>
                    )}

                    {/* ── Nav buttons ── */}
                    <div className={classes.btnWrap}>
                        {step > 0 && (
                            <button
                                className={classes.btnSecondary}
                                onClick={handleBack}
                                disabled={saving}
                                id="ob-qf-back"
                            >
                                Previous
                            </button>
                        )}
                        <button
                            className={classes.btnPrimary}
                            onClick={isLastStep ? handleSubmit : handleNext}
                            disabled={saving}
                            id={isLastStep ? 'ob-qf-submit' : 'ob-qf-next'}
                        >
                            {saving ? 'Saving...' : isLastStep ? 'Create Profile' : 'Next'}
                        </button>
                    </div>

                    <div className={classes.centerText}>
                        Step {step + 1} of {STEPS.length}
                    </div>

                    <div className='w-100 d-flex justify-content-center'>
                        <button className='btn btn-sm btn-link' onClick={handleLogout} style={{ fontSize: '12px' }}>Login with a different phone number?</button>
                    </div>


                </div>

            </div>
        </div>
    );
};

export default OnboardingQuickForm;
